import { CSSProperties } from 'react'
import { PlanType } from 'modules/Const'
import ModalSymphony from 'elements/ModalSymphony'
import ButtonSymphony from 'elements/ButtonSymphony'
import useScreen from 'Hooks/useScreen'
import { Body2, Subtitle2 } from 'components/shareable/Typography'
import Colors from 'modules/Colors'
import { styx } from 'modules/Utils'
import useBrand from 'Hooks/useBrand'
import useOrganizationV2 from 'Hooks/useOrganizationV2'

interface CancellationConfirmationModalProps {
    /** Controls modal visibility */
    isOpen: boolean
    /** Callback when modal is closed */
    onClose: () => void
    /** Callback when cancellation is confirmed */
    onConfirm: () => void
    /** Current plan type */
    currentPlan: PlanType
    /** Whether the user is on a grandfathered plan */
    isGrandfathered: boolean
    /** Whether cancellation is in progress */
    isLoading?: boolean
}

const CancellationConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    currentPlan,
    isGrandfathered,
    isLoading = false
}: CancellationConfirmationModalProps) => {
    const { mobileView } = useScreen()
    const { currentBrand } = useBrand()
    const { organization } = useOrganizationV2()

    // Get current subscription status - check organization first (for team plans), then brand
    const getCurrentSubscriptionStatus = (): string | null => {
        // For team plans, check organization status
        if (currentPlan === PlanType.TEAM || currentPlan === PlanType.TEAM_V2) {
            return organization?.stripe_subscription_status || null
        }
        // For individual plans, check brand status
        return currentBrand?.subscription_status || currentBrand?.subscription?.status || null
    }

    const subscriptionStatus = getCurrentSubscriptionStatus()

    // Determine if cancellation will be immediate based on subscription status
    // These statuses require immediate cancellation per Stripe API constraints
    const problematicStatuses = ['paused', 'past_due', 'unpaid', 'incomplete', 'incomplete_expired'];
    const willCancelImmediately = subscriptionStatus && problematicStatuses.includes(subscriptionStatus);

    // Get appropriate messaging based on cancellation type
    const getCancellationMessage = () => {
        if (willCancelImmediately) {
            return {
                subtitle: `Your subscription will be cancelled immediately due to its ${subscriptionStatus} status.`,
                confirmButtonText: 'Yes, Cancel Now',
                warningText: subscriptionStatus === 'paused' 
                    ? 'Since your subscription is paused, it will be cancelled immediately rather than at the end of your billing cycle.'
                    : `Since your subscription is ${subscriptionStatus}, it will be cancelled immediately.`
            };
        }
        return {
            subtitle: 'Your subscription will be cancelled at the end of your billing cycle.',
            confirmButtonText: 'Yes, Cancel Subscription',
            warningText: null
        };
    };

    const cancellationInfo = getCancellationMessage();

    /**
     * Get benefits that will be lost based on plan type
     */
    const getBenefitsToLose = (plan: PlanType, grandfathered: boolean): string[] => {
        if (grandfathered) {
            switch (plan) {
                case PlanType.LITE:
                    return [
                        'Access to AI campaign creation',
                        'Basic website builder',
                        'Campaign analytics and reporting',
                        'Email support'
                    ]
                case PlanType.PRO:
                    return [
                        'Unlimited AI campaign creation',
                        'Advanced website builder with custom domains',
                        'Advanced analytics and insights',
                        'AI asset generation (images, videos)',
                        'Multiple team members',
                        'Priority support'
                    ]
                case PlanType.TEAM:
                    return [
                        'All Pro features',
                        'Advanced team collaboration',
                        'Team member management',
                        'Advanced reporting and analytics',
                        'Dedicated account manager',
                        'Custom integrations'
                    ]
                default:
                    return ['Access to paid features']
            }
        } else {
            switch (plan) {
                case PlanType.STARTER:
                    return [
                        '5 AI actions per month',
                        'Basic campaign creation',
                        'Website builder access',
                        'Email support'
                    ]
                case PlanType.GROWTH:
                    return [
                        '15 AI actions per month',
                        'Advanced campaign targeting',
                        'Premium website templates',
                        'AI asset generation',
                        'Analytics and insights',
                        'Priority support'
                    ]
                case PlanType.TEAM_V2:
                    return [
                        '50 AI actions per month',
                        'Advanced team features',
                        'Multi-user collaboration',
                        'Advanced analytics',
                        'Custom integrations',
                        'Dedicated support'
                    ]
                default:
                    return ['Access to paid features']
            }
        }
    }

    const benefits = getBenefitsToLose(currentPlan, isGrandfathered)
    const planDisplayName = currentPlan === PlanType.TEAM_V2 ? 'Team' : currentPlan

    return (
        <ModalSymphony
            className="cancellationConfirmationModal"
            isOpen={isOpen}
            onClose={onClose}
            size="medium"
            fullScreen={mobileView}
            title="Are you sure you want to cancel your subscription?"
            subtitle={cancellationInfo.subtitle}
        >
            <div className="cancellationModalContent" style={sty.container}>
                {/* Show warning text for immediate cancellations */}
                {cancellationInfo.warningText && (
                    <div className="cancellationWarning" style={sty.warningBox}>
                        <Body2 style={sty.warningText}>
                            {cancellationInfo.warningText}
                        </Body2>
                    </div>
                )}

                {/* <div className="cancellationModalBenefits" style={sty.benefitsList}>
                    {benefits.map((benefit, index) => (
                        <div key={index} className="cancellationModalBenefit" style={sty.benefitItem}>
                            <div style={sty.bulletPoint}>•</div>
                            <Body2 style={sty.benefitText}>{benefit}</Body2>
                        </div>
                    ))}
                </div> */}

                <div className="cancellationModalActions" style={sty.actions}>
                    <ButtonSymphony
                        className="cancellationModalCancelButton"
                        onClick={onClose}
                        variant="outlined"
                        disabled={isLoading}
                        style={sty.cancelButton}
                    >
                        Keep Subscription
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="cancellationModalConfirmButton"
                        onClick={onConfirm}
                        color="red"
                        isLoading={isLoading}
                        disabled={isLoading}
                        loadingText={willCancelImmediately ? "Canceling Now..." : "Canceling..."}
                    >
                        {cancellationInfo.confirmButtonText}
                    </ButtonSymphony>
                </div>
            </div>
        </ModalSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: 24
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        textAlign: 'center'
    },
    title: {
        fontWeight: 600,
        color: Colors.black
    },
    subtitle: {
        color: Colors.grey
    },
    benefitsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '16px 0'
    },
    benefitItem: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8
    },
    bulletPoint: {
        color: Colors.purple,
        fontWeight: 600,
        fontSize: 16,
        lineHeight: '24px',
        minWidth: 8
    },
    benefitText: {
        color: Colors.black,
        flex: 1
    },
    actions: {
        display: 'flex',
        flexDirection: 'row',
        gap: 12,
        justifyContent: 'flex-end'
    },
    cancelButton: {
        minWidth: 140
    },
    warningBox: {
        backgroundColor: '#FEF3C7', // Light yellow background
        border: '1px solid #F59E0B', // Yellow border
        borderRadius: 8,
        padding: 16,
        marginBottom: 16
    },
    warningText: {
        color: '#92400E', // Dark yellow/brown text
        fontSize: 14,
        lineHeight: '20px',
        margin: 0
    }
}

export default CancellationConfirmationModal 